import React, {
    forwardRef, JSXElementConstructor,
    MouseEvent,
    PropsWithChildren,
    ReactElement,
    Ref,
    useCallback,
    useMemo,
    useRef,
    useState
} from "react"
import {
    Checkbox,
    CheckboxProps,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    makeStyles, MenuItem,
    Radio, Select,
    TextField,
    TextFieldProps,
    Typography,
    TypographyProps
} from "@material-ui/core"
import { FormikValues, useField, useFormikContext } from "formik"
import { StrictOmit, StringKeyOf } from "shared/Types/helperTypes"
import { StandardLoadingButton, StandardLoadingButtonProps } from "../Loading/LoadingButton"
import { StandardFloatingButton } from "../Button/StandardFloatingButton"
import {
    IDeliveryForm,
    IFormikSelectOptionsProps,
    IFormikSelectProps
} from "../../Types/MeetingPlanning/deliveryForm"
import { Autocomplete } from "@material-ui/lab"
import AsteriskComponent from "../Layout/Asterisk"
import { DatePicker, DatePickerProps, MuiPickersUtilsProvider } from "@material-ui/pickers"
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect"
import { DateTime } from "luxon"
import LuxonUtils from "@date-io/luxon"
import { useLocale } from "../../Modules/Localization/useLocale"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar } from "@fortawesome/pro-light-svg-icons/faCalendar"
import { CustomDay } from "../../Modules/MeetingPlanning/Components/Form/CustomDay"
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"
import { IOpeningHour } from "../../Types/MeetingPlanning/meetingServiceConfiguration"
import clsx from "clsx"

export type FormikTextFieldProps<Values extends FormikValues> = Readonly<
    StrictOmit<TextFieldProps, "name" | "value" | "error" | "onBlur" | "onChange"> & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik
        title?: string
        required?: boolean
        active?: boolean
    }
>

type FormikDatePickerProps<Values extends Record<string, any>, TDate> = {
    name: keyof Values
    label?: string
    placeholder?: string
    required?: boolean
    disabled?: boolean
    openOnInit?: boolean
    closingDates: string[]
    allowedWeekdays: IOpeningHour[]
} & Omit<DatePickerProps, "value" | "onChange" | "renderInput">

const useStyles = makeStyles((theme) => ({
    cell: {
        width: '32px',
        height: '32px',
        margin: '2px'
    },
    fullWidth: {
      width: '100%'
    },
    textInput: {
        minHeight: "4rem",
    },
    mb_1: {
        marginBottom: theme.spacing(1),
    },
    placeholder: {
        color: theme.palette.text.disabled,
        fontStyle: 'normal',
    },
    formSelect: {
        '& .MuiSelect-select:focus': {
            backgroundColor: 'transparent'
        }
    },
    formSelectWithoutLabel: {
        '& legend span': {
            display: 'none'
        }
    }
}))

export function FormikTextField<Values extends FormikValues>({ name, label, placeholder, helperText, active = true, required, ...rest }: FormikTextFieldProps<Values>) {
    const classes = useStyles()
    const [field, meta] = useField<string>(name)

    return active ? (
        <>
            {placeholder && label && (
                <Typography variant="body1" className={classes.mb_1}>
                    {label}
                    <AsteriskComponent required={required || false} />
                </Typography>
            )}
            <TextField
                name={name}
                value={field.value}
                label={!placeholder && label ? label : ''}
                placeholder={placeholder}
                error={meta.touched && Boolean(meta.error)}
                helperText={(meta.touched && meta.error) || helperText}
                onBlur={field.onBlur}
                onChange={field.onChange}
                className={classes.textInput}
                {...rest}
            />
        </>
    ) : <></>
}

export type FormikCheckboxProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<
        StrictOmit<CheckboxProps, "name" | "value" | "checked" | "onChange"> & {
            name: StringKeyOf<Values>
            value?: string | number | readonly string[] // MUI types value prop as unknown but Formik only supports these types
            helperText?: string
            typographyVariant?: TypographyProps["variant"]
            onLabelClick?: (e: MouseEvent<HTMLLabelElement>) => void
        }
    >
>

export function FormikCheckbox<Values extends FormikValues>({
    name,
    value,
    helperText,
    typographyVariant,
    children,
    onLabelClick,
    ...rest
}: FormikCheckboxProps<Values>) {
    const [field, meta] = useField({ type: "checkbox", name, value })

    return (
        <FormControl error={Boolean(meta.error)}>
            <FormControlLabel
                label={<Typography variant={typographyVariant}>{children}</Typography>}
                control={
                    <Checkbox name={name} value={value} checked={field.checked} onChange={field.onChange} {...rest} />
                }
                onClick={onLabelClick}
            />
            <FormHelperText>{meta.error || helperText}</FormHelperText>
        </FormControl>
    )
}

type FormikRadioListItemProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<{
        name: StringKeyOf<Values>
        value: string | number | undefined
    }>
>

export function FormikRadioListItem<Values extends FormikValues>({
    name,
    value,
    children,
}: FormikRadioListItemProps<Values>) {
    const [field, _, helpers] = useField({ type: "radio", name, value })

    return (
        <>
            <ListItem button onClick={() => helpers.setValue(value)}>
                <ListItemText primary={children} />
                <ListItemSecondaryAction>
                    <Radio name={name} value={value} checked={field.checked} onChange={() => helpers.setValue(value)} />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
        </>
    )
}

export const FormikSelectControl = ({ name, label, isFloatingLabel, required, placeholder, options, ...props }: IFormikSelectProps) => {
    const classes = useStyles()
    const [field, meta] = useField(name)

    return (
        <>
            {
                (label && !isFloatingLabel) && (
                    <Typography variant="body1" className={classes.mb_1}>
                        {label}
                        <AsteriskComponent required={required || false} />
                    </Typography>
                )
            }
            <FormControl fullWidth variant="outlined" error={meta.touched && Boolean(meta.error)}>
                {
                    label && isFloatingLabel && (
                        <InputLabel>
                            {label}
                            <AsteriskComponent required={required || false} />
                        </InputLabel>
                    )
                }
                <Select
                    {...field}
                    displayEmpty
                    className={clsx(classes.formSelect, { [classes.formSelectWithoutLabel]: label && !isFloatingLabel })}
                    label={label}
                    {...props}
                >
                    {placeholder && (
                        <MenuItem value="" disabled hidden>
                            <em className={classes.placeholder}>{placeholder}</em>
                        </MenuItem>
                    )}
                    {options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
                {meta.touched && meta.error && (
                    <FormHelperText>{meta.error}</FormHelperText>
                )}
            </FormControl>
        </>
    )
}

export const FormikAutocompleteControl = ({ name, label, placeholder, options, required }: IFormikSelectProps) => {
    const classes = useStyles()
    const [field, meta] = useField(name)
    const { setFieldValue } = useFormikContext()

    const selectedOption = useMemo<IFormikSelectOptionsProps | null>(
        () => options.find(opt => opt.value === field.value) || null,
        [options, field.value]
    )

    const onBlur = useCallback(() => field.onBlur({ target: { name } }), [field, name])

    return (
        <>
            {(label && placeholder) && (
                <Typography variant="body1" className={classes.mb_1}>
                    {label}
                    <AsteriskComponent required={required || false} />
                </Typography>
            )}
            <FormControl fullWidth variant="outlined" error={meta.touched && Boolean(meta.error)}>
                <Autocomplete
                    fullWidth
                    options={options}
                    value={selectedOption}
                    getOptionLabel={(option: IFormikSelectOptionsProps) => option.label || ""}
                    onBlur={onBlur}
                    onChange={(_, newValue: IFormikSelectOptionsProps | null) => setFieldValue(name, newValue?.value ?? "")}
                    renderOption={(option) => (
                        <Typography>{option.label}</Typography>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={label && !placeholder ? label : ''}
                            placeholder={placeholder || ''}
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched ? meta.error : undefined}
                        />
                    )}
                />
            </FormControl>
        </>
    )
}

type FormikSubmitButtonProps = StrictOmit<StandardLoadingButtonProps, "loading">

export const FormikSubmitButton = forwardRef<HTMLButtonElement, FormikSubmitButtonProps>(
    ({ disabled, children, ...rest }, ref: Ref<HTMLButtonElement>) => {
        const { isValid, isSubmitting, submitCount } = useFormikContext();

        const isDisabled =
            disabled ||
            (submitCount > 0 && !isValid);

        return (
            <StandardLoadingButton
                ref={ref}
                type="submit"
                disabled={isDisabled}
                loading={isSubmitting}
                {...rest}
            >
                {children}
            </StandardLoadingButton>
        );
    }
)

export const FormikFloatingSubmitButton = forwardRef(
    ({ disabled, children, ...rest }: FormikSubmitButtonProps, ref: Ref<HTMLButtonElement>) => {
        const { isValid, isSubmitting, submitCount } = useFormikContext();

        const isDisabled =
            disabled ||
            (submitCount > 0 && !isValid);

        return (
            <StandardFloatingButton
                ref={ref}
                type="submit"
                disabled={isDisabled}
                loading={isSubmitting}
                {...rest}
            >
                {children}
            </StandardFloatingButton>
        )
    }
)

export function FormikDatePickerControlled<Values extends Record<string, any>, TDate>(
    { name, required, placeholder, label, disabled = false, openOnInit, closingDates, allowedWeekdays, ...rest }: FormikDatePickerProps<Values, TDate>,
) {
    const classes = useStyles()
    const inputRef = useRef<HTMLInputElement>(null)
    const [field, meta] = useField(name)
    const { setFieldValue, setFieldTouched } = useFormikContext<IDeliveryForm>()
    const [open, setOpen] = useState(false)
    const locale = useLocale()

    const allowedDays = useMemo(() => allowedWeekdays?.length ? allowedWeekdays.map((item) => item.weekDayNumber) : [], [allowedWeekdays?.length])

    const handleChange = (value: DateTime | null) => {
        setFieldValue(name, value?.toISODate() ?? "")
        setOpen(false)
    }

    useDebouncedEffect(() => {
        if (openOnInit && !field.value) {
            setOpen(true)
        }
    }, [field.value, openOnInit])

    const handleBlur = useCallback(() => {
        setFieldTouched(name, true, true)
    }, [name, setFieldTouched])

    const toDateTime = (d: MaterialUiPickersDate): DateTime | undefined => {
        if (!d) return undefined;

        return d || DateTime.fromJSDate(d as Date)
    }

    const shouldDisableDate = (day: MaterialUiPickersDate): boolean => {
        if (!day) return false;

        const dt = toDateTime(day);
        if (!dt) return false;

        const today = DateTime.now().startOf('day');
        const closedDatesDT = closingDates.map((d) =>
            DateTime.fromFormat(d, 'yyyy.MM.dd')
        );

        const isClosed = closedDatesDT.some((d) => d.hasSame(dt, 'day'));
        const notAllowedWeekday = !allowedDays.includes(dt.weekday);
        const isPast = rest.disablePast && dt < today;

        return Boolean(isClosed || notAllowedWeekday || isPast);
    };

    const renderDay = (
        day: MaterialUiPickersDate,
        selectedDate: MaterialUiPickersDate,
        dayInCurrentMonth: boolean,
        dayComponent: ReactElement<any, string | JSXElementConstructor<any>>
    ) => dayInCurrentMonth ? (
        <CustomDay
            day={day}
            selectedDate={selectedDate}
            closingDates={closingDates}
            allowedWeekdays={allowedDays}
            dayComponent={dayComponent}
        />
    ) : <div className={classes.cell} />;

    return (
        <>
            {label && placeholder ? (
                <Typography variant="body1" className={classes.mb_1}>
                    {label}
                    <AsteriskComponent required={required || false} />
                </Typography>
            ) : null}
            <MuiPickersUtilsProvider utils={LuxonUtils} locale={locale}>
                <FormControl className={classes.fullWidth} error={Boolean(meta.error)}>
                    <DatePicker
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        value={field.value ? DateTime.fromISO(field.value) : null}
                        onChange={handleChange}
                        onAccept={handleChange}
                        autoOk
                        label={label}
                        disabled={disabled || false}
                        inputVariant="outlined"
                        fullWidth
                        format="dd/MM/yyyy"
                        minDate={DateTime.now().startOf("day")}
                        shouldDisableDate={shouldDisableDate}
                        renderDay={renderDay}
                        TextFieldComponent={(props) => (
                            <TextField
                                {...props}
                                inputRef={inputRef}
                                label={label && !placeholder ? label : ''}
                                variant="outlined"
                                fullWidth
                                placeholder={placeholder || ''}
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched ? meta.error : undefined}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    setOpen(true)
                                    handleBlur()
                                }}
                                InputProps={{
                                    ...props.InputProps,
                                    endAdornment: (
                                        <FontAwesomeIcon icon={faCalendar} />
                                    ),
                                }}
                            />
                        )}
                        {...rest}
                    />
                </FormControl>
            </MuiPickersUtilsProvider>
        </>
    )
}
